import { BIBLE_BOOKS } from './bibleBooks';
import { localDateOnly } from './localDate';
import { toChips } from './parseReference';
import { mergeRecaps } from './recap';

const asyncUpdateNote = (isAsync) => (
  isAsync ? ' Plan updates are processing in the background.' : ''
);

const plural = (count, word) => `${count} ${word}${count === 1 ? '' : 's'}`;

/** "Today", "Yesterday" or "Sep 28" for a YYYY-MM-DD string. */
export function formatDateChip(dateString, today = localDateOnly()) {
  if (dateString === today) return 'Today';
  const [y, m, d] = dateString.split('-').map(Number);
  const [ty, tm, td] = today.split('-').map(Number);
  if (localDateOnly(new Date(ty, tm - 1, td - 1)) === dateString) return 'Yesterday';
  return new Date(y, m - 1, d).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/** "Brandon", "Smith (4)", "Ann, Ben" or "Ann, Ben +1". */
export function describeReaders({ readerMode, selectedFamily, selectedReaders }) {
  if (readerMode === 'family' && selectedFamily) {
    return `${selectedFamily.name} (${selectedFamily.readers?.length || selectedReaders.length})`;
  }
  const names = selectedReaders.map(r => r.name);
  if (names.length === 0) return 'Choose readers';
  if (names.length <= 2) return names.join(', ');
  return `${names.slice(0, 2).join(', ')} +${names.length - 2}`;
}

/** Chapter id -> {chapterId, book, chapter}, from the static book table. */
export function chapterFromId(chapterId) {
  const book = BIBLE_BOOKS.find(b => chapterId >= b.startId && chapterId < b.startId + b.chapters);
  if (!book) return null;
  return { chapterId, book: book.name, chapter: chapterId - book.startId + 1 };
}

/** "Genesis 1-3, Psalm 23" for the chapter ids, in Bible order. */
export function describeChapters(chapterIds) {
  const entries = [...chapterIds]
    .sort((a, b) => a - b)
    .map(chapterFromId)
    .filter(Boolean);
  return toChips(entries).map(chip => chip.label).join(', ');
}

/**
 * The text on the submit button: what is about to be logged, or null when
 * nothing is selected yet.
 */
export function submitLabel({ mode, chapterIds, books }) {
  if (mode === 'books') {
    if (books.length === 0) return null;
    const names = books.length <= 2 ? books.join(', ') : plural(books.length, 'book');
    return `Log ${names}`;
  }
  if (chapterIds.length === 0) return null;
  const description = describeChapters(chapterIds);
  const short = description.length > 28 ? plural(chapterIds.length, 'chapter') : description;
  return `Log ${short} · ${plural(chapterIds.length, 'chapter')}`
    .replace(/^(Log .+) · 1 chapter$/, '$1');
}

/**
 * Create the reports for one submit. Returns what the page needs to show:
 * `message`, the before -> after `recap`, plus the post-log `moment` and
 * `reportIds` for chapter logs.
 *
 * `api` is the reportApi (passed in so the payloads can be tested without
 * the network layer); `onProgress` receives partial progress updates.
 */
export async function submitLog({
  mode,
  readers,
  readerMode,
  family,
  chapterIds,
  books,
  readDate,
  notes,
  details,
  onProgress = () => {},
  api
}) {
  const reportData = {
    readDate,
    notes: notes?.trim() || undefined,
    ...details,
    recap: true
  };
  const useFamily = readerMode === 'family' && family;

  const withReaders = (data) => {
    if (readers.length === 1) return { ...data, readerId: readers[0].id };
    return { ...data, readerIds: readers.map(r => r.id) };
  };

  if (mode === 'books') {
    onProgress({
      stage: 'books',
      current: 0,
      total: books.length,
      totalReports: books.length * readers.length,
      estimatedDuration: books.length * Math.max(2, Math.ceil(readers.length * 0.5)),
      message: `Preparing to log ${plural(books.length, 'book')} for ${plural(readers.length, 'reader')}...`
    });

    // A session lets the server batch the plan updates across the books.
    const sessionId = `books-${Date.now()}`;
    let sessionStarted = false;
    if (books.length > 1) {
      try {
        await api.startSession(sessionId, books.length);
        sessionStarted = true;
      } catch (err) {
        console.warn('Failed to start session, continuing without:', err);
      }
    }

    const results = [];
    try {
      for (let i = 0; i < books.length; i++) {
        const book = books[i];
        onProgress({ current: i + 1, message: `Logging ${book} (${i + 1} of ${books.length})...` });
        const data = { ...reportData, book, sessionId: sessionStarted ? sessionId : undefined };
        const result = useFamily
          ? await api.createFamilyReport(family.id, data)
          : await api.createReport(withReaders(data));
        results.push(result.data);
      }
    } finally {
      if (sessionStarted) {
        try {
          await api.endSession(sessionId);
        } catch (err) {
          console.warn('Failed to end session:', err);
        }
      }
    }

    const created = results.reduce((sum, r) => sum + (r.summary?.reportsCreated || 0), 0);
    const skipped = results.reduce((sum, r) => sum + (r.summary?.skipped || 0), 0);
    const isAsync = results.some(r => r.summary?.processingMode === 'asynchronous');
    return {
      message:
        `Logged ${plural(books.length, 'book')} for ${plural(readers.length, 'reader')}: ` +
        `${plural(created, 'chapter')}${skipped > 0 ? `, skipped ${plural(skipped, 'duplicate')}` : ''}.` +
        asyncUpdateNote(isAsync),
      recap: mergeRecaps(results.map(r => r.recap)),
      moment: null,
      reportIds: []
    };
  }

  const total = chapterIds.length * readers.length;
  onProgress({
    stage: 'chapters',
    current: 0,
    total,
    totalReports: total,
    estimatedDuration: Math.max(3, Math.ceil(total * 0.1)),
    message: `Logging ${plural(total, 'chapter')} for ${plural(readers.length, 'reader')}...`
  });

  const data = { ...reportData, chapterIds };
  const result = useFamily
    ? await api.createFamilyReport(family.id, data)
    : await api.createReport(withReaders(data));

  return {
    message: result.data.message + asyncUpdateNote(result.data.summary?.processingMode === 'asynchronous'),
    recap: result.data.recap || null,
    moment: result.data.moment || null,
    reportIds: (result.data.reports || []).map(r => r.id)
  };
}
